<template>
  <div class="container">
    <header>
      <h1><i class="ri-book-2-line"></i> AI 学习笔记
        <span v-if="isToday" class="today-tag">今天</span>
      </h1>
    </header>

    <!-- 日期导航 -->
    <div class="date-nav">
      <a
        v-for="d in recentPills"
        :key="d"
        class="date-pill"
        :class="{ active: d === selectedDate }"
        @click="selectDate(d)"
      >{{ formatDatePill(d) }}</a>
      <input
        type="date"
        class="date-input"
        v-model="dateInput"
        :min="minDate"
        :max="maxDate"
        title="跳转到指定日期"
        @change="onDateInput"
      />
    </div>

    <!-- 快捷条 -->
    <div class="back-bar">
      <a class="back-link" @click="goToday"><i class="ri-calendar-line"></i> 今天</a>
      <a class="back-link" @click="randomNote"><i class="ri-dice-line"></i> 随机一篇</a>
      <a class="back-link" @click="goStats"><i class="ri-bar-chart-2-line"></i> 统计</a>
      <a class="back-link" @click="goAbout"><i class="ri-user-line"></i> 关于我</a>
    </div>

    <!-- 复习提醒：今日待复习（艾宾浩斯 1/2/4/7/15 天） -->
    <div v-if="dueReviews.length" class="review-card">
      <div class="hm-title"><i class="ri-alarm-line"></i> 今日待复习 <span class="hm-sub">{{ dueReviews.length }} 篇到复习周期</span></div>
      <a
        v-for="r in dueReviews"
        :key="r.push_date"
        class="review-item"
        @click="goDetail(r.push_date)"
      >
        <span class="review-item-title">{{ r.title }}</span>
        <span class="review-item-meta">{{ r.days }} 天前读过</span>
      </a>
    </div>

    <!-- 统计卡 -->
    <div class="stats">
      <div class="stat-card"><div class="num">{{ filtered.length }}</div><div class="label">条目</div></div>
      <div class="stat-card"><div class="num">{{ topicCount }}</div><div class="label">主题</div></div>
      <div class="stat-card"><div class="num">{{ total }}</div><div class="label">总计</div></div>
      <div class="stat-card"><div class="num">{{ latestDateShort || '-' }}</div><div class="label">最新</div></div>
    </div>

    <!-- 学习热力图 -->
    <div v-if="yearList.length" class="hm-wrap">
      <div class="hm-title"><i class="ri-calendar-check-line"></i> 学习热力图 <span class="hm-sub">{{ yearLabel }} · 学习 {{ learnedDays }} 天 · 共 {{ totalWords }} 字</span></div>
      <div class="hm-months">
        <span class="hm-months-corner"></span>
        <span v-for="(w, wi) in heatWeeks" :key="'m' + wi" class="hm-mon">{{ w.monthLabel }}</span>
      </div>
      <div class="hm-body">
        <div class="hm-weekdays">
          <span class="hm-wd">一</span><span class="hm-wd"></span><span class="hm-wd">三</span><span class="hm-wd"></span><span class="hm-wd">五</span><span class="hm-wd"></span><span class="hm-wd"></span>
        </div>
        <div class="hm-grid">
          <div v-for="(w, wi) in heatWeeks" :key="'c' + wi" class="hm-col">
            <div
              v-for="d in w.days"
              :key="d.ymd"
              class="hm-cell"
              :class="'lv' + d.level"
              :title="d.title"
            ></div>
          </div>
        </div>
      </div>
      <div class="hm-legend">
        <span>少</span>
        <span class="hm-cell"></span>
        <span class="hm-cell lv1"></span>
        <span class="hm-cell lv2"></span>
        <span class="hm-cell lv3"></span>
        <span class="hm-cell lv4"></span>
        <span>多</span>
      </div>
    </div>

    <!-- 主题分布 -->
    <div v-if="topicDist.length" class="dist-wrap">
      <div class="hm-title"><i class="ri-bar-chart-box-line"></i> 主题分布 <span class="hm-sub">近一年 · {{ topicDist.length }} 个主题</span></div>
      <div v-for="t in topicDist" :key="t.code" class="dist-row">
        <span class="dist-name" :title="t.name">{{ t.name }}</span>
        <div class="dist-bar-bg"><div class="dist-bar" :style="{ width: t.pct + '%' }"></div></div>
        <span class="dist-count">{{ t.count }}</span>
      </div>
    </div>

    <!-- 主题筛选 -->
    <div class="source-bar" v-if="topicList.length">
      <span
        class="source-badge"
        :class="{ active: activeTopic === '全部' }"
        @click="setTopic('全部')"
      >全部</span>
      <span
        v-for="t in visibleTopics"
        :key="t.topic_code"
        class="source-badge"
        :class="{ active: activeTopic === t.topic_code }"
        @click="setTopic(t.topic_code)"
      >{{ shortTopicName(t.topic_code, t.topic_name) }}</span>
      <span
        v-if="topicList.length > TOPIC_SHOW_COUNT"
        class="source-badge more-badge"
        @click="topicExpanded = !topicExpanded"
      >{{ topicExpanded ? '收起 ▲' : '更多 ▼' }}</span>
    </div>

    <!-- 标签云（可折叠） -->
    <div class="tag-cloud-bar" v-if="tagCloud.length">
      <span
        class="source-badge tag-cloud-toggle"
        @click="tagExpanded = !tagExpanded"
      ><i class="ri-hashtag"></i> 标签云 {{ tagExpanded ? '▲' : '▼' }}</span>
      <template v-if="tagExpanded">
        <span
          v-for="t in tagCloud"
          :key="t.tag"
          class="source-badge"
          :class="{ active: activeTag === t.tag }"
          @click="applyTag(t.tag)"
        >{{ t.tag }} {{ t.count }}</span>
      </template>
      <span v-if="activeTag" class="source-badge more-badge" @click="clearTag">清除标签 ×</span>
    </div>

    <!-- 搜索 + 排序 -->
    <div class="controls">
      <input type="text" v-model="keyword" placeholder="搜索标题、主题..." @input="onSearch" />
      <select v-model="sortBy" @change="rerender">
        <option value="newest">最新优先</option>
        <option value="hottest">最热优先</option>
      </select>
      <span
        class="source-badge fav-toggle"
        :class="{ active: favOnly }"
        @click="favOnly = !favOnly"
        title="只看我收藏的笔记（收藏保存在本地浏览器）"
      ><i :class="favOnly ? 'ri-star-fill' : 'ri-star-line'"></i> 收藏<template v-if="favCount"> {{ favCount }}</template></span>
    </div>

    <!-- 收藏管理条（只看收藏时显示） -->
    <div v-if="favOnly" class="fav-bar">
      <span class="fav-bar-info"><i class="ri-star-line"></i> 已收藏 {{ favCount }} 篇 · 保存在本地浏览器</span>
      <span class="fav-bar-actions">
        <a class="back-link" @click="onExportFav" title="导出收藏为 JSON 文件"><i class="ri-download-2-line"></i> 导出</a>
        <a class="back-link" @click="$refs.favFile.click()" title="从 JSON 文件导入收藏"><i class="ri-upload-2-line"></i> 导入</a>
      </span>
      <input ref="favFile" type="file" accept=".json,application/json" style="display:none" @change="onImportFile" />
    </div>
    <div v-if="favMsg" class="fav-msg">{{ favMsg }}</div>

    <!-- 内容区 -->
    <div v-if="loading" class="loading">加载中…</div>
    <div v-else-if="error" class="error-tip">{{ error }}</div>
    <!-- 搜索模式：服务端分页结果 -->
    <div v-else-if="searchActive">
      <div class="search-result-bar">
        <span>「{{ keyword.trim() }}」共 {{ searchTotal }} 条结果</span>
        <a class="back-link" @click="clearSearch"><i class="ri-close-line"></i> 清除搜索</a>
      </div>
      <div v-if="searching" class="loading">搜索中…</div>
      <template v-else>
        <div v-if="!searchResults.length" class="empty"><i class="ri-inbox-line"></i> 没有匹配的条目</div>
        <div
          v-for="d in searchResults"
          :key="d.push_date"
          class="entry spotlight"
          @click="goDetail(d.push_date)"
        >
          <div class="title"><a>{{ d.title }}</a></div>
          <div class="subtitle" v-if="d.summary">{{ d.summary }}</div>
          <div class="meta">
            <span class="source">{{ d.topic_type || '默认' }}</span>
            <span>{{ formatDatePill(d.push_date) }}</span>
            <span v-if="d.view_count" class="meta-views"><i class="ri-eye-line"></i> {{ d.view_count }}</span>
          </div>
        </div>
        <!-- 分页 -->
        <div v-if="searchTotal > searchSize" class="pager">
          <a class="pager-btn" :class="{ disabled: searchPage <= 1 }" @click="goSearchPage(searchPage - 1)"><i class="ri-arrow-left-s-line"></i> 上一页</a>
          <span class="pager-info">{{ searchPage }} / {{ searchPages }}</span>
          <a class="pager-btn" :class="{ disabled: searchPage >= searchPages }" @click="goSearchPage(searchPage + 1)">下一页 <i class="ri-arrow-right-s-line"></i></a>
        </div>
      </template>
    </div>
    <div v-else>
      <div v-if="!filtered.length" class="empty"><i class="ri-inbox-line"></i> 没有匹配的条目</div>
      <div
        v-for="d in filtered"
        :key="d.push_date"
        class="entry spotlight"
        @click="goDetail(d.push_date)"
      >
        <div class="title"><a>{{ d.title }}</a></div>
        <div class="subtitle" v-if="d.summary">{{ d.summary }}</div>
        <div class="meta">
          <span class="source">{{ d.topic_type || '默认' }}</span>
          <span>{{ formatDatePill(d.push_date) }}</span>
          <span v-if="d.view_count" class="meta-views"><i class="ri-eye-line"></i> {{ d.view_count }}</span>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer>
      <p>更新日期 {{ latestDate || '-' }} · 收录 {{ total }} 篇 · {{ topicCount }} 个主题</p>
      <p class="footer-links">
        <a href="/api/rss.xml" target="_blank" rel="noopener" title="RSS 订阅"><i class="ri-rss-line"></i> RSS 订阅</a>
      </p>
      <div class="beian">
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener">闽ICP备2026026930号-1</a>
        <br>
        <a href="https://beian.mps.gov.cn/#/query/webSearch?code=35010202002243" target="_blank" rel="noopener noreferrer">
          <img src="/static/beian-logo.png" style="vertical-align:middle;width:16px;height:16px;margin-right:4px;" alt="公安备案标识" />
          闽公网安备35010202002243号
        </a>
      </div>
    </footer>
  </div>
</template>

<script>
import aiPush from '../api/aiPush'
import fav from '../utils/favorites'
import review from '../utils/review'

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const MONTHS = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
const PILL_COUNT = 7 // 近几天用 pill
const TOPIC_SHOW_COUNT = 5 // 主题筛选条默认显示的个数（其余折叠进"更多"）

// 热力图强度：按当日笔记字数分级（0=未学习）
function levelOf (words) {
  if (!words) return 0
  if (words < 600) return 1
  if (words < 1400) return 2
  if (words < 2400) return 3
  return 4
}

// 主题筛选条短名映射（详情页仍用完整 topic_name，这里仅筛选条展示用）
const TOPIC_SHORT = {
  prompt: 'Prompt',
  rag: 'RAG',
  llm_principle: 'LLM 原理',
  agent: 'Agent',
  model_select: '模型选型',
  vectordb: '向量库',
  app_practice: '应用实战',
  finetune: '微调',
  alignment: '安全对齐',
  multimodal: '多模态'
}

export default {
  name: 'HomePage',
  props: { initialTag: { type: String, default: '' } },
  data () {
    return {
      loading: false,
      error: '',
      list: [],          // 全部已发布条目（精简字段）
      total: 0,
      yearList: [],      // 近一年条目（热力图/主题分布用，含 word_count）
      allDates: [],      // 全部日期倒序
      topicList: [],     // 主题字典
      activeTopic: '全部',
      keyword: '',
      sortBy: 'newest',
      selectedDate: '',  // 当前选中的日期（用于 pill 高亮，空表示全部）
      dateInput: '',     // 日历跳转输入（type=date）
      topicExpanded: false,  // 主题筛选条是否展开（默认折叠）
      tagCloud: [],          // 标签云 [{tag, count}]
      tagExpanded: false,    // 标签云是否展开（默认折叠）
      activeTag: '',         // 当前激活的标签（服务端筛选）
      tagItems: [],          // 标签筛选结果
      favOnly: false,        // 只看收藏
      favMsg: '',            // 收藏导入/导出提示
      // 服务端搜索状态
      searchResults: [],
      searchTotal: 0,
      searchPage: 1,
      searchSize: 10,
      searching: false,
      searchTimer: null
    }
  },
  computed: {
    recentPills () {
      return this.allDates.slice(0, PILL_COUNT)
    },
    minDate () {
      return this.allDates[this.allDates.length - 1] || ''
    },
    maxDate () {
      return this.allDates[0] || ''
    },
    isToday () {
      const today = this.formatYmd(new Date())
      return this.selectedDate === today
    },
    topicCount () {
      return this.topicList.length
    },
    /** 是否处于搜索模式（有非空关键词） */
    searchActive () {
      return this.keyword.trim().length > 0
    },
    /** 搜索总页数 */
    searchPages () {
      return Math.max(1, Math.ceil(this.searchTotal / this.searchSize))
    },
    topicShowCount () {
      return TOPIC_SHOW_COUNT
    },
    visibleTopics () {
      // 默认只显示前 TOPIC_SHOW_COUNT 个，展开后显示全部
      return this.topicExpanded ? this.topicList : this.topicList.slice(0, TOPIC_SHOW_COUNT)
    },
    latestDate () {
      return this.allDates[0] || ''
    },
    latestDateShort () {
      return this.latestDate ? this.latestDate.slice(5) : ''
    },
    /** 收藏数量（响应 favState 变化） */
    favCount () {
      return fav.favState.items.length
    },
    /** 今日待复习（响应 readState 变化） */
    dueReviews () {
      // 触发响应式依赖
      void review.readState.items.length
      return review.dueList()
    },
    filtered () {
      // 标签筛选走服务端结果集；否则用本地全量列表
      let arr = this.activeTag ? [...this.tagItems] : [...this.list]
      if (this.favOnly) {
        const set = fav.dateSet()
        arr = arr.filter(d => set.has(d.push_date))
      }
      if (this.activeTopic !== '全部') {
        arr = arr.filter(d => d.topic_code === this.activeTopic)
      }
      const q = this.keyword.trim().toLowerCase()
      if (q) {
        arr = arr.filter(d =>
          (d.title || '').toLowerCase().includes(q) ||
          (d.topic_type || '').toLowerCase().includes(q) ||
          (d.summary || '').toLowerCase().includes(q)
        )
      }
      // 排序：最新优先按日期倒序；最热优先按阅读量降序（同量时日期倒序）
      if (this.sortBy === 'hottest') {
        arr.sort((a, b) => {
          const dv = (Number(b.view_count) || 0) - (Number(a.view_count) || 0)
          return dv !== 0 ? dv : (a.push_date < b.push_date ? 1 : -1)
        })
      } else {
        arr.sort((a, b) => (a.push_date < b.push_date ? 1 : -1))
      }
      return arr
    },
    // ── 学习数据（近一年） ──
    heatWeeks () {
      const map = {}
      ;(this.yearList || []).forEach(d => { map[d.push_date] = { title: d.title, words: Number(d.word_count) || 0 } })
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const start = new Date(today)
      start.setDate(start.getDate() - 363)
      // 对齐到周一（getDay: 0=周日，转 0=周一）
      const dow = (start.getDay() + 6) % 7
      start.setDate(start.getDate() - dow)
      const weeks = []
      const cur = new Date(start)
      let prevMonthKey = -1
      while (cur.getTime() <= today.getTime()) {
        const week = { monthLabel: '', days: [] }
        const first = new Date(cur)
        const mKey = first.getFullYear() * 12 + first.getMonth()
        if (mKey !== prevMonthKey) {
          week.monthLabel = MONTHS[first.getMonth()]
          prevMonthKey = mKey
        }
        for (let i = 0; i < 7; i++) {
          const dt = new Date(cur)
          const ymd = this.formatYmd(dt)
          const info = map[ymd]
          let level = 0
          let title = ymd
          if (info) {
            level = levelOf(info.words)
            title = `${ymd} · ${info.title} · ${info.words} 字`
          }
          week.days.push({ ymd, level, title })
          cur.setDate(cur.getDate() + 1)
        }
        weeks.push(week)
      }
      return weeks
    },
    learnedDays () {
      const set = new Set((this.yearList || []).map(d => d.push_date))
      return set.size
    },
    totalWords () {
      return (this.yearList || []).reduce((s, d) => s + (Number(d.word_count) || 0), 0)
    },
    yearLabel () {
      const y = new Date().getFullYear()
      return `${y - 1}.${new Date().getMonth() + 1} - ${y}.${new Date().getMonth() + 1}`
    },
    topicDist () {
      const names = {}
      this.topicList.forEach(t => { names[t.topic_code] = t.topic_name })
      const counts = {}
      ;(this.yearList || []).forEach(d => {
        const code = d.topic_code || 'default'
        if (!counts[code]) {
          counts[code] = {
            code,
            name: this.shortTopicName(code, names[code]) || code,
            count: 0
          }
        }
        counts[code].count++
      })
      const arr = Object.values(counts).sort((a, b) => b.count - a.count)
      const max = arr.length ? arr[0].count : 1
      arr.forEach(a => { a.pct = Math.round((a.count / max) * 100) })
      return arr
    }
  },
  mounted () {
    this.init()
  },
  watch: {
    // 从详情页标签 pill 跳转进来时激活标签筛选
    initialTag (val) {
      if (val) {
        this.applyTag(val)
      } else if (this.activeTag) {
        this.clearTag()
      }
    }
  },
  methods: {
    async init () {
      this.loading = true
      this.error = ''
      try {
        await Promise.all([this.loadTopics(), this.loadDates(), this.loadList(), this.loadYearData(), this.loadTagCloud()])
        // 初始携带标签（#/notes/tag/:tag）
        if (this.initialTag) {
          await this.applyTag(this.initialTag)
        }
      } catch (e) {
        this.error = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async loadTopics () {
      const { data } = await aiPush.topics()
      this.topicList = data || []
    },
    /** 标签云加载（失败静默：后端未上线 tags 接口时不影响主流程） */
    async loadTagCloud () {
      try {
        const { data } = await aiPush.tags()
        this.tagCloud = data || []
      } catch (e) {
        this.tagCloud = []
      }
    },
    /** 激活标签筛选（服务端查询），再次点击同一标签则取消 */
    async applyTag (tag) {
      if (!tag) return
      if (this.activeTag === tag) {
        this.clearTag()
        return
      }
      this.activeTag = tag
      this.tagExpanded = true
      try {
        const { data } = await aiPush.listByTag(tag, 1, 200)
        this.tagItems = data || []
      } catch (e) {
        this.tagItems = []
        this.flashFavMsg('标签筛选失败：' + (e.message || '请求失败'))
      }
    },
    clearTag () {
      this.activeTag = ''
      this.tagItems = []
    },
    async loadDates () {
      const { data } = await aiPush.dateList(60)
      this.allDates = data || []
    },
    async loadList () {
      // 拉较多条目用于本地过滤/搜索（与原 app.js 的 DATA 本地过滤体验一致）
      const { data, total } = await aiPush.list({ page: 1, size: 200 })
      this.list = data || []
      this.total = total || this.list.length
    },
    async loadYearData () {
      // 近一年全量（含 word_count），供热力图与主题分布
      const end = this.formatYmd(new Date())
      const start = new Date()
      start.setFullYear(start.getFullYear() - 1)
      const { data } = await aiPush.list({
        startDate: this.formatYmd(start),
        endDate: end,
        page: 1,
        size: 400
      })
      this.yearList = data || []
    },
    setTopic (code) {
      this.activeTopic = code
      this.rerender()
    },
    onSearch () {
      // 防抖：有输入走服务端分页搜索，清空则回本地列表
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        if (this.searchActive) {
          this.searchPage = 1
          this.doSearch()
        }
      }, 300)
    },
    /** 服务端搜索 */
    async doSearch () {
      const kw = this.keyword.trim()
      if (!kw) return
      this.searching = true
      try {
        const { data, total } = await aiPush.list({
          keyword: kw,
          page: this.searchPage,
          size: this.searchSize
        })
        this.searchResults = data || []
        this.searchTotal = total || 0
      } catch (e) {
        this.searchResults = []
        this.searchTotal = 0
        this.flashFavMsg('搜索失败：' + (e.message || '请求失败'))
      } finally {
        this.searching = false
      }
    },
    goSearchPage (p) {
      if (p < 1 || p > this.searchPages || p === this.searchPage) return
      this.searchPage = p
      this.doSearch()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    clearSearch () {
      this.keyword = ''
      this.searchResults = []
      this.searchTotal = 0
      this.searchPage = 1
    },
    rerender () {
      // 触发 filtered 重新计算（computed 自动响应，此处占位）
    },
    selectDate (d) {
      this.selectedDate = d
      this.goDetail(d)
    },
    onDateInput () {
      // 保留所选值，让日历持续展示已选日期
      if (this.dateInput) {
        this.selectDate(this.dateInput)
      }
    },
    goToday () {
      const today = this.formatYmd(new Date())
      if (this.allDates.includes(today)) {
        this.selectDate(today)
      } else if (this.allDates.length) {
        this.selectDate(this.allDates[0])
      }
    },
    goDetail (pushDate) {
      window.location.hash = `#/date/${pushDate}`
    },
    randomNote () {
      if (!this.allDates.length) return
      const d = this.allDates[Math.floor(Math.random() * this.allDates.length)]
      this.goDetail(d)
    },
    goAbout () {
      window.location.hash = '#/about'
    },
    goStats () {
      window.location.hash = '#/stats'
    },
    onExportFav () {
      if (!this.favCount) {
        this.flashFavMsg('还没有收藏，先在详情页点 ☆ 收藏')
        return
      }
      fav.exportJson()
      this.flashFavMsg('已导出 aid-favorites.json')
    },
    async onImportFile (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = '' // 允许重复选同一文件
      if (!file) return
      try {
        const added = await fav.importJson(file)
        this.flashFavMsg(`导入成功：新增 ${added} 条收藏`)
      } catch (err) {
        this.flashFavMsg('导入失败：' + (err.message || '文件格式错误'))
      }
    },
    flashFavMsg (msg) {
      this.favMsg = msg
      clearTimeout(this._favMsgTimer)
      this._favMsgTimer = setTimeout(() => { this.favMsg = '' }, 3000)
    },
    formatDatePill (d) {
      if (!d) return ''
      const dt = new Date(d.replace(/-/g, '/'))
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      return `${m}-${day} ${WEEKDAYS[dt.getDay()]}`
    },
    formatYmd (dt) {
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const d = String(dt.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    /** 筛选条用短名（详情页仍用完整 topic_name） */
    shortTopicName (code, fullName) {
      return TOPIC_SHORT[code] || fullName || '默认'
    }
  }
}
</script>
